import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, MapPin, Linkedin, Facebook, Instagram, Twitter, MessageCircle, X } from "lucide-react";
import Reveal from "./Reveal";
import { useSite } from "@/context/SiteContext";
import { Logo } from "./Navbar";
import { track } from "@/lib/api";

function TikTokIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64c.3 0 .6.05.88.13V9.4a6.33 6.33 0 0 0-1-.05A6.34 6.34 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
    </svg>
  );
}

export function FooterCta() {
  return (
    <section
      className="relative overflow-hidden py-24"
      style={{ backgroundImage: "url(/unified-cta-bg.png)", backgroundSize: "cover", backgroundPosition: "center" }}
    >
      <div className="relative max-w-[1400px] mx-auto px-5 lg:px-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-10">
        <Reveal direction="left">
          <h2 className="font-display font-black text-white tracking-tight sm:whitespace-nowrap" style={{ fontSize: "clamp(20px, 5vw, 56px)", lineHeight: 1.12 }}>
            Build a team that <span className="text-gradient">works like</span> yours.
          </h2>
          <p className="mt-5 text-slate-300 text-xl">Tell us what you need. Let's find the right support.</p>
        </Reveal>
      </div>
    </section>
  );
}

const socials = [
  { icon: Linkedin, label: "LinkedIn" },
  { icon: Facebook, label: "Facebook" },
  { icon: Twitter, label: "X" },
  { icon: Instagram, label: "Instagram" },
];

export default function Footer() {
  return (
    <footer
      className="relative overflow-hidden"
      style={{ backgroundImage: "url(/footer-bg.png)", backgroundSize: "cover", backgroundPosition: "center" }}
    >
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{ backgroundImage: "url(/footer-bg.png)", backgroundSize: "cover", backgroundPosition: "center", filter: "blur(18px)", transform: "scale(1.12)" }}
      />
      <div aria-hidden="true" className="absolute inset-0 bg-[rgba(4,10,26,0.7)]" />
      <div className="relative z-10 max-w-[1400px] mx-auto px-5 lg:px-10 py-10 grid md:grid-cols-[1.15fr_0.85fr_1.25fr_1fr] gap-10 md:gap-0">
        <div className="flex justify-center md:pr-8">
          <Logo size={120} />
        </div>
        <div className="md:border-l md:border-white/10 md:pl-10">
          <div className="eyebrow text-[#00CFFF] mb-5">Explore</div>
          <div className="flex flex-col gap-3.5">
            <a href="/about" className="text-[15px] font-semibold text-white hover:text-cyan-300 transition-colors duration-200 w-fit">About Us</a>
            <a href="/apply" className="text-[15px] font-semibold text-white hover:text-cyan-300 transition-colors duration-200 w-fit">Apply Now</a>
            <a href="#services" className="text-[15px] font-semibold text-white hover:text-cyan-300 transition-colors duration-200 w-fit">What We Do</a>
            <a href="#process" className="text-[15px] font-semibold text-white hover:text-cyan-300 transition-colors duration-200 w-fit">Our Process</a>
            <a href="#team" className="text-[15px] font-semibold text-white hover:text-cyan-300 transition-colors duration-200 w-fit">Our Team</a>
          </div>
        </div>
        <div className="md:border-l md:border-white/10 md:pl-10">
          <div className="eyebrow text-[#00CFFF] mb-5">Contact</div>
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <span className="w-9 h-9 rounded-full bg-[#0A2238] border border-[rgba(0,207,255,0.35)] flex items-center justify-center flex-shrink-0"><Mail size={14} className="text-cyan-300" /></span>
              <span className="text-[15px] text-white">info@TrustDesk.com</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="w-9 h-9 rounded-full bg-[#0A2238] border border-[rgba(0,207,255,0.35)] flex items-center justify-center flex-shrink-0"><MapPin size={14} className="text-cyan-300" /></span>
              <span className="text-[15px] text-white">Remote • Philippines</span>
            </div>
          </div>
        </div>
        <div className="md:border-l md:border-white/10 md:pl-10">
          <div className="eyebrow text-[#00CFFF] mb-5">Follow Us</div>
          <div className="flex gap-2.5 flex-nowrap">
            {socials.map((s) => (
              <span key={s.label} aria-label={s.label} className="w-10 h-10 rounded-full bg-[#0A1E33] border border-white/10 flex items-center justify-center flex-shrink-0 text-slate-200 hover:text-white hover:bg-[#1D6FF2] hover:border-[#1D6FF2] transition-all duration-200 cursor-pointer">
                <s.icon size={16} />
              </span>
            ))}
            <span aria-label="TikTok" className="w-10 h-10 rounded-full bg-[#0A1E33] border border-white/10 flex items-center justify-center flex-shrink-0 text-slate-200 hover:text-white hover:bg-[#1D6FF2] hover:border-[#1D6FF2] transition-all duration-200 cursor-pointer">
              <TikTokIcon size={16} />
            </span>
          </div>
        </div>
      </div>
      <div className="relative border-t border-white/10">
        <div className="max-w-[1400px] mx-auto pl-5 pr-24 sm:pl-10 sm:pr-28 py-4 flex flex-col sm:flex-row items-center justify-between gap-3 text-[13px] text-white/85">
          <div>© 2026 Trust Desk. All rights reserved.</div>
          <div className="flex items-center gap-3">
            <span className="hover:text-white cursor-pointer transition-colors">Privacy Policy</span>
            <span className="text-white/40">|</span>
            <span className="hover:text-white cursor-pointer transition-colors">Terms of Service</span>
          </div>
        </div>
      </div>
    </footer>
  );
}

export function ChatBubble() {
  const { openBooking } = useSite();
  const [open, setOpen] = useState(false);
  return (
    <div className="fixed bottom-5 right-5 z-[80] flex flex-col items-end gap-3" data-testid="chat-bubble-toggle">
      {open && (
        <motion.div
          initial={{ opacity: 0, y: 16, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          className="w-72 rounded-2xl glass-dark p-5 shadow-2xl"
        >
          <div className="flex items-center justify-between">
            <span className="font-display font-extrabold text-white text-sm">Need a hand?</span>
            <button onClick={() => setOpen(false)} aria-label="Close chat" data-testid="chat-bubble-close"><X size={16} className="text-slate-400 hover:text-white" /></button>
          </div>
          <p className="mt-2 text-xs text-slate-300 leading-relaxed">
            Tell us what's on your plate and we'll match you with a specialist within 48 hours.
          </p>
          <button
            className="btn btn-sm btn-primary w-full mt-4"
            onClick={() => { setOpen(false); track("chat_book_click"); openBooking(null, "booking"); }}
            data-testid="chat-bubble-book-button"
          >
            Book a Free Consultation
          </button>
        </motion.div>
      )}
      <motion.button
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.94 }}
        onClick={() => setOpen((o) => !o)}
        className="w-16 h-16 rounded-full flex items-center justify-center text-white shadow-xl shadow-blue-600/40"
        style={{ background: "linear-gradient(135deg,#2E9BFF,#1D4ED8)" }}
        aria-label="Chat with us"
        data-testid="chat-bubble-button"
      >
        {open ? <X size={24} /> : <MessageCircle size={24} />}
      </motion.button>
    </div>
  );
}
